import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { brl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PayMethods } from "@/components/brand/SiteFooter";

export const hasAnnual = (p) => !!p.annual_enabled && Number(p.price_annual) > 0;
export const hasMonthly = (p) => p.monthly_enabled !== false;
export const discountPct = (p) => Math.max(0, Math.round((1 - Number(p.price_annual) / (Number(p.price) * 12 || 1)) * 100));
const KW = (p, per) => !!p[`kiwify_${per}_url`] && Number(p[`kiwify_${per}_price`]) > 0;
export const view = (p, channel) => (channel === "kiwify"
  ? { ...p, name: p.kiwify_name || p.name, price: Number(p.kiwify_monthly_price) || 0, price_annual: Number(p.kiwify_annual_price) || 0, monthly_enabled: KW(p, "monthly"), annual_enabled: KW(p, "annual") }
  : p);

const PeriodToggle = ({ period, setPeriod, best }) => (
  <div className="inline-flex rounded-full border p-1 bg-card" data-testid="period-toggle">
    {[["monthly", "Mensal"], ["annual", "Anual"]].map(([k, l]) => (
      <button key={k} type="button" onClick={() => setPeriod(k)} data-testid={`period-${k}`}
        className={`px-4 h-9 rounded-full text-sm transition-colors ${period === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
        {l}{k === "annual" && best > 0 && <span className={`ml-1.5 text-[11px] font-semibold ${period === k ? "" : "text-[#34A853]"}`}>-{best}%</span>}
      </button>
    ))}
  </div>
);

const Price = ({ p, period }) => period === "annual" ? (
  <div className="mt-6" data-testid="plan-price">
    <p><span className="text-3xl font-semibold">{brl(p.price_annual / 12)}</span><span className="text-sm opacity-60">/mês</span></p>
    <p className="text-xs text-muted-foreground mt-1">{brl(p.price_annual)} cobrados por ano
      {p.price * 12 > p.price_annual && <span className="text-[#34A853] font-medium"> · economize {brl(p.price * 12 - p.price_annual)}</span>}</p>
  </div>
) : <p className="mt-6" data-testid="plan-price"><span className="text-3xl font-semibold">{brl(p.price)}</span><span className="text-sm opacity-60">/mês</span></p>;

export default function PlanCards({ onSelect, currentPlanId, busyId, channel = "mercadopago" }) {
  const [raw, setPlans] = useState(null);
  const [kwOn, setKwOn] = useState(false);
  const [period, setPeriod] = useState("monthly");
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
    api.get("/public/config").then((r) => setKwOn(!!r.data.payment_channels?.kiwify)).catch(() => {});
  }, []);
  if (!raw) return null;
  const plans = raw.map((p) => view(p, channel));
  const annual = plans.filter(hasAnnual);
  const best = Math.max(0, ...annual.map(discountPct));
  const shown = plans.filter(period === "annual" ? hasAnnual : hasMonthly);
  const pick = (p) => (onSelect ? onSelect(p, period) : nav(`${user ? "/app/assinatura" : "/cadastro"}?plan=${p.id}&period=${period}`));

  return (
    <div className="space-y-6">
      {annual.length > 0 && <PeriodToggle period={period} setPeriod={setPeriod} best={best} />}
      {!shown.length && <p className="text-sm text-muted-foreground" data-testid="plans-empty">Nenhum plano disponível nesta modalidade no momento.</p>}
      <div className="grid md:grid-cols-3 gap-5" data-testid="plan-cards">
        {shown.map((p) => (
          <div key={p.id} data-testid={`plan-card-${p.name.toLowerCase()}`}
            className={`relative rounded-xl p-6 sm:p-7 flex flex-col border bg-card transition-shadow duration-200 hover:shadow-md ${p.highlighted ? "border-primary ring-1 ring-primary shadow-md" : ""}`}>
            {p.highlighted && <span className="absolute -top-3 left-7 px-3 py-1 rounded-full text-[11px] font-mono bg-primary text-primary-foreground">Mais escolhido</span>}
            <h3 className="text-xl font-semibold">{p.name}</h3>
            <p className="text-sm mt-1 text-muted-foreground">{p.description}</p>
            <Price p={p} period={period} />
            {channel === "mercadopago" && kwOn && KW(raw.find((x) => x.id === p.id), period) && <p className="text-xs text-muted-foreground mt-1" data-testid="plan-kiwify-price">Pela Kiwify: {brl(raw.find((x) => x.id === p.id)[`kiwify_${period}_price`])}{period === "annual" ? "/ano" : "/mês"}</p>}
            <PayMethods className="mt-2" />
            <ul className="mt-6 space-y-2.5 text-sm flex-1">
              {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-[#34A853] mt-0.5 shrink-0" />{f}</li>)}
            </ul>
            <Button data-testid={`plan-select-${p.name.toLowerCase()}`} disabled={currentPlanId === p.id || busyId === p.id} onClick={() => pick(p)}
              variant={p.highlighted ? "default" : "outline"} className="mt-8 h-11 rounded-full">
              {currentPlanId === p.id ? "Plano atual" : busyId === p.id ? "Abrindo pagamento..." : p.cta_label || "Assinar agora"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
